/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { FileUpload } from './FileUpload';
import { DataPreview } from './DataPreview';
import { FieldMapping } from './FieldMapping';
import { ImportSummary } from './ImportSummary';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';

interface ParsedData {
  headers: string[];
  rows: any[][];
  filename: string;
}

interface FieldMapping {
  internalField: string;
  mappedColumn: string | null;
  required: boolean;
}

type ImportStep = 'upload' | 'preview' | 'mapping' | 'summary';

interface Props {
  onBackToList: () => void;
}

export default function ImportMapping({ onBackToList }: Props) {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [processedData, setProcessedData] = useState<any[]>([]);

  const steps = [
    { key: 'upload', label: 'Upload File', completed: !!parsedData },
    {
      key: 'preview',
      label: 'Preview Data',
      completed: fieldMappings.length > 0,
    },
    {
      key: 'mapping',
      label: 'Map Fields',
      completed: processedData.length > 0,
    },
    { key: 'summary', label: 'Import Summary', completed: false },
  ];

  const handleFileUploaded = (data: ParsedData) => {
    setParsedData(data);
    setCurrentStep('preview');
  };

  const handlePreviewConfirmed = () => {
    setCurrentStep('mapping');
  };

  const handleMappingComplete = (
    mappings: FieldMapping[],
    processed: any[],
  ) => {
    setFieldMappings(mappings);
    setProcessedData(processed);
    setCurrentStep('summary');
  };

  const resetImport = () => {
    setParsedData(null);
    setFieldMappings([]);
    setProcessedData([]);
    setCurrentStep('upload');
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Data Import Mapping Tool
        </h1>
        <p className="text-muted-foreground">
          Upload your CSV or Excel file and map columns to your database fields
        </p>
      </div>

      {/* Progress Steps */}
      <Card className="mb-8 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                    step.completed
                      ? 'bg-success border-success text-success-foreground'
                      : currentStep === step.key
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-background border-border text-muted-foreground'
                  }`}
                >
                  {step.completed ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="ml-3">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-foreground">
                      {step.label}
                    </div>
                  </div>
                  {step.completed && (
                    <Badge variant="secondary" className="mt-1">
                      Complete
                    </Badge>
                  )}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`hidden md:block mx-8 h-0.5 w-24 transition-colors ${
                    step.completed ? 'bg-success' : 'bg-border'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Step Content */}
      {currentStep === 'upload' && (
        <FileUpload onFileUploaded={handleFileUploaded} />
      )}

      {currentStep === 'preview' && parsedData && (
        <DataPreview
          data={parsedData}
          onConfirm={handlePreviewConfirmed}
          onBack={() => setCurrentStep('upload')}
        />
      )}

      {currentStep === 'mapping' && parsedData && (
        <FieldMapping
          data={parsedData}
          onMappingComplete={handleMappingComplete}
          onBack={() => setCurrentStep('preview')}
        />
      )}

      {currentStep === 'summary' && (
        <ImportSummary
          data={processedData}
          filename={parsedData?.filename || ''}
          onNewImport={resetImport}
          onBackToList={onBackToList}
        />
      )}
    </div>
  );
}
